import { useEffect, useRef, useState } from "react";
import { loadYMaps } from "../../../lib/ymapsLoader";
import s from "./YandexMap.module.scss";

type Props = {
  title?: string;
  address?: string;
  coords?: [number, number]; // [lat, lng]
  zoom?: number;
};

function clampZoom(z?: number) {
  if (!z || Number.isNaN(z)) return 16;
  return Math.max(0, Math.min(19, z));
}

export default function YandexMap({
  title = "Стоматология",
  address = "",
  coords,
  zoom = 16,
}: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const placemarkRef = useRef<any>(null);

  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!hostRef.current) return;

      const apiKey = process.env.REACT_APP_YMAPS_API_KEY || "";
      if (!apiKey) {
        setState("error");
        setErr("Не задан REACT_APP_YMAPS_API_KEY");
        return;
      }

      setState("loading");

      try {
        const ym = await loadYMaps(apiKey, "ru_RU");
        if (cancelled) return;

        // Получаем координаты: либо из props, либо через geocode [5](https://yandex.com/dev/jsapi-v2-1/doc/en/v2-1/ref/reference/geocode)[6](https://yandex.ru/dev/maps/jsapi/doc/2.1-dev/ref/reference/multiRouter.MultiRoute-docpage/)
        let center: [number, number] | null = coords ?? null;

        if (!center && address) {
          const res = await ym.geocode(address);
          const first = res.geoObjects.get(0);
          if (first) center = first.geometry.getCoordinates();
        }

        if (!center) center = [55.76, 37.64]; // fallback

        if (!mapRef.current) {
          // Карта создаётся после готовности API (через ymaps.ready внутри лоадера) [2](https://yandex.com/maps-api/docs/js-api/quickstart.html)[1](https://yandex.com/maps-api/docs/js-api/dg/concepts/load.html)
          const map = new ym.Map(hostRef.current, {
            center,
            zoom: clampZoom(zoom),
            controls: ["zoomControl", "fullscreenControl"],
          });

          mapRef.current = map;

          // Метка с hint/balloon [3](https://snipp.ru/js/yamaps-balloon)[4](https://yandex.com/dev/jsapi-v2-1/doc/en/v2-1/ref/reference/Placemark)
          const pm = new ym.Placemark(
            center,
            {
              hintContent: title,
              balloonContentBody: [
                `<strong>${title}</strong>`,
                address ? `<div style="margin-top:6px">${address}</div>` : "",
              ].join(""),
            },
            { preset: "islands#redDotIcon" }
          );

          placemarkRef.current = pm;
          map.geoObjects.add(pm);

          pm.events.add("click", () => pm.balloon.open());
        } else {
          mapRef.current.setCenter(center, clampZoom(zoom));
          if (placemarkRef.current) placemarkRef.current.geometry.setCoordinates(center);
        }

        setState("ready");
      } catch (e: any) {
        setState("error");
        setErr(e?.message || "Ошибка загрузки карты");
      }
    }

    init();

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
        placemarkRef.current = null;
      }
    };
  }, [title, address, coords, zoom]);

  return (
    <div className={s.wrap}>
      <div className={s.card}>
        {state === "error" ? (
          <div className={s.fallback}>
            <div className={s.fallbackTitle}>Карта не загрузилась</div>
            <div className={s.fallbackText}>{err}</div>
          </div>
        ) : (
          <div ref={hostRef} className={s.map} />
        )}
      </div>

      {state === "loading" && <div className={s.loading}>Загрузка карты…</div>}
    </div>
  );
}
